import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import {
  CheckoutContextType,
  CheckoutProviderProps,
  CouponDiscount,
  ExtraData,
  OrderPrices,
  Product
} from '../Checkout.types';

export const CheckoutContext = createContext<CheckoutContextType | null>(null);

export const useCheckout = () => {
  const context = useContext(CheckoutContext);
  if (context === null) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }
  return context;
};

export const CheckoutProvider = ({
  children,
  totalSteps,
  config,
  initialProducts = [],
  onCouponApply,
  onCouponRemove
}: CheckoutProviderProps) => {
  // Step navigation
  const [currentStep, setCurrentStep] = useState(1);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  // Products (always reflects parent `initialProducts`, e.g. subscription search updates)
  const userProducts = initialProducts;
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);

  // Pricing
  const [orderPrices, setOrderPrices] = useState<OrderPrices>({});

  // Coupon
  const [couponDiscount, setCouponDiscount] = useState<CouponDiscount | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);

  // Payment
  const [paymentMethod, setPaymentMethod] = useState<'klarna' | 'invoice' | undefined>(undefined);

  // Anonymous not logged in / dialog state
  const [startedAsAnonymous, setStartedAsAnonymous] = useState(false);
  const [openSelectProductDialog, setOpenSelectProductDialog] = useState(false);

  // Extra data
  const [extraData, setExtraData] = useState<ExtraData>({});
  const [orderStepFieldErrors, setOrderStepFieldErrors] = useState<{ [key: string]: string }>({});

  // --- Step navigation ---
  const openCheckout = useCallback(() => setIsCheckoutOpen(true), []);
  const closeCheckout = useCallback(() => setIsCheckoutOpen(false), []);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => (prev < totalSteps ? prev + 1 : prev));
  }, [totalSteps]);

  const previousStep = useCallback(() => {
    setCurrentStep((prev) => (prev > 1 ? prev - 1 : prev));
  }, []);

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 1 && step <= totalSteps) {
        setCurrentStep(step);
      }
    },
    [totalSteps]
  );

  // --- Product selection ---
  const addSelectedProduct = useCallback((product: Product) => {
    setSelectedProducts((prev) => {
      if (prev.some((p) => p.id === product.id)) {
        return prev;
      }
      return [...prev, product];
    });
  }, []);

  const removeSelectedProduct = useCallback((product: Product) => {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== product.id));
  }, []);

  // --- Coupon handling (wraps parent callbacks with error state) ---
  const handleCouponApply = useCallback(
    async (code: string) => {
      setCouponError(null);
      try {
        if (config.fetchCouponDiscount) {
          const discount = await config.fetchCouponDiscount(code, selectedProducts, extraData);
          setCouponDiscount(discount);
        } else {
          await onCouponApply?.(code);
        }
      } catch (err) {
        setCouponError(err instanceof Error ? err.message : 'Ogiltig rabattkod');
      }
    },
    [config, onCouponApply, selectedProducts, extraData]
  );

  const handleCouponRemove = useCallback(() => {
    setCouponDiscount(null);
    setCouponError(null);
    onCouponRemove?.();
  }, [onCouponRemove]);

  // --- Reset ---
  const resetCheckout = useCallback(() => {
    setIsCheckoutOpen(false);
    setCurrentStep(1);
    setSelectedProducts([]);
    setOrderPrices({});
    setPaymentMethod(undefined);
    setCouponDiscount(null);
    setCouponError(null);
    setExtraData({});
    setOrderStepFieldErrors({});
    setStartedAsAnonymous(false);
    setOpenSelectProductDialog(false);
  }, []);

  // --- Context value (memoized to prevent unnecessary re-renders) ---
  const contextValue = useMemo<CheckoutContextType>(
    () => ({
      currentStep,
      isCheckoutOpen,
      nextStep,
      previousStep,
      goToStep,
      openCheckout,
      closeCheckout,
      resetCheckout,

      userProducts,
      selectedProducts,
      addSelectedProduct,
      removeSelectedProduct,
      setSelectedProducts,

      orderPrices,
      setOrderPrices,

      couponDiscount,
      setCouponDiscount,
      onCouponApply: handleCouponApply,
      onCouponRemove: handleCouponRemove,
      couponError,

      paymentMethod,
      setPaymentMethod,

      startedAsAnonymous,
      setStartedAsAnonymous,

      openSelectProductDialog,
      setOpenSelectProductDialog,

      config,

      extraData,
      setExtraData,

      orderStepFieldErrors,
      setOrderStepFieldErrors
    }),
    [
      currentStep,
      isCheckoutOpen,
      nextStep,
      previousStep,
      goToStep,
      openCheckout,
      closeCheckout,
      resetCheckout,
      userProducts,
      selectedProducts,
      addSelectedProduct,
      removeSelectedProduct,
      orderPrices,
      couponDiscount,
      handleCouponApply,
      handleCouponRemove,
      couponError,
      paymentMethod,
      startedAsAnonymous,
      openSelectProductDialog,
      config,
      extraData,
      orderStepFieldErrors
    ]
  );

  return <CheckoutContext.Provider value={contextValue}>{children}</CheckoutContext.Provider>;
};
